/**
 * EventCategoriesSection Component
 * 
 * Displays active event categories (Decorating Classes, Birthday Parties, etc.)
 * on the homepage. Only shows categories that are:
 * - is_active = true
 * - has_landing_page = true (optional - can show all or just those with landing pages)
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

// API URL
const API_URL = import.meta.env.VITE_API_URL || '';

interface EventCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  image_url: string | null;
  color: string;
  has_landing_page: boolean;
  is_active: boolean;
}

interface EventCategoriesSectionProps {
  /** Only show categories with landing pages */
  landingPagesOnly?: boolean;
  /** Custom title for the section */
  title?: string;
  /** Custom subtitle */
  subtitle?: string;
  /** Maximum number of categories to show */
  limit?: number;
}

export default function EventCategoriesSection({ 
  landingPagesOnly = false, 
  title = "Services & Events",
  subtitle = "Join us for special classes and events",
  limit = 2
}: EventCategoriesSectionProps) {
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/event-categories?active=true`);
        if (!response.ok) return;
        
        const data = await response.json();
        let items = data.categories || [];
        
        // Filter for landing pages if needed
        if (landingPagesOnly) {
          items = items.filter((c: EventCategory) => c.has_landing_page);
        }
        
        // Apply limit
        if (limit) {
          items = items.slice(0, limit);
        }
        
        setCategories(items);
      } catch (err) {
        console.error('Failed to fetch event categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [landingPagesOnly, limit]);

  // Don't render anything if no active categories
  if (loading || categories.length === 0) {
    return null;
  }

  return (
    <section className="section-padding bg-gradient-to-br from-purple-50 via-pink-50 to-bakery-cream">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-white/80 px-4 py-2 rounded-full mb-4 shadow-sm">
            <span className="text-2xl">🎉</span>
            <span className="font-medium text-gray-700">Special Events</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className={`grid gap-6 ${
          categories.length === 1 ? 'max-w-md mx-auto' :
          categories.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' :
          'md:grid-cols-3'
        }`}>
          {categories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              {category.has_landing_page ? (
                <Link
                  to={`/events/${category.slug}`}
                  className="group block bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                  style={{ borderTop: `4px solid ${category.color}` }}
                >
                  {/* Image */}
                  <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100">
                    {category.image_url ? (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-6xl">
                        🎂
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-display font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      {category.name}
                    </h3>
                    {category.description && (
                      <p className="text-gray-600 mb-4 line-clamp-2">
                        {category.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-primary-600 font-semibold group-hover:underline">
                      <span>Learn More</span>
                      <ArrowRightIcon className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              ) : (
                <div
                  className="bg-white rounded-2xl overflow-hidden shadow-md p-6"
                  style={{ borderTop: `4px solid ${category.color}` }}
                >
                  {/* Image */}
                  {category.image_url && (
                    <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg mb-4">
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <h3 className="text-xl font-display font-bold text-gray-900 mb-2">
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="text-gray-600 mb-4">
                      {category.description}
                    </p>
                  )}
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:underline"
                  >
                    <span>Contact Us</span>
                    <ArrowRightIcon className="w-5 h-5" />
                  </Link>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
